import { useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import { nn } from "date-fns/locale";
import { CalendarDays, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { toISODate } from "@/lib/format";

const parse = (iso) => {
  if (!iso) return undefined;
  const d = parseISO(iso);
  return isValid(d) ? d : undefined;
};

/**
 * ISO `yyyy-MM-dd` in/out (local time, no UTC round-trip). The calendar opens on the chosen date,
 * otherwise on `defaultMonth` (e.g. the sibling start date) so a «Til dato» field follows «Frå dato».
 */
export const DateField = ({ value, onChange, defaultMonth, placeholder = "dd.mm.åååå", className, disabled, "data-testid": testId, ...aria }) => {
  const [open, setOpen] = useState(false);
  const selected = parse(value);
  const openMonth = selected || parse(defaultMonth) || new Date();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            {...aria}
            data-testid={testId}
            data-value={value || ""}
            className={cn(
              "flex h-11 w-full items-center gap-2 rounded-[8px] border border-input bg-card px-3.5 py-1 text-left text-base transition-colors hover:border-foreground/50 focus-visible:border-foreground focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive md:text-sm",
              !selected && "text-muted-foreground",
              selected && "pr-9",
              className,
            )}
          >
            <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="flex-1 truncate tabular-nums">{selected ? format(selected, "dd.MM.yyyy") : placeholder}</span>
          </button>
        </PopoverTrigger>
        {selected && !disabled && (
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label="Tøm dato"
            title="Tøm dato"
            className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:bg-secondary focus-visible:text-foreground focus-visible:outline-none"
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {/* Focus the target day from here (not DayPicker's initialFocus): that fires before this popover is the active focus layer, so the Sheet's focus trap would yank focus back to the trigger and Radix would then treat it as an outside interaction. */}
      {/* fixedWeeks keeps every month the same height, so the popover never flips above/below the field while paging months; the max-height lets it scroll instead of leaving the viewport when space is short. */}
      <PopoverContent className="w-auto max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-2" align="start" collisionPadding={8} onOpenAutoFocus={(e) => { e.preventDefault(); e.currentTarget?.querySelector('button[name="day"][tabindex="0"]')?.focus(); }} data-testid={testId ? `${testId}-popover` : undefined}>
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={openMonth}
          onSelect={(d) => { if (d) onChange(toISODate(d)); setOpen(false); }}
          locale={nn}
          weekStartsOn={1}
          fixedWeeks
        />
      </PopoverContent>
    </Popover>
  );
};
